/**
 * Marks a control that handles Enter itself, so the wizard must not also advance when it is pressed.
 *
 * Write it as a literal `data-consumes-enter-key={true}` on an input whose field binds Enter to its
 * own action — a search box, say. Dropdowns and textareas are recognized without it.
 *
 * Only `"true"` opts a control out. Setting it to `"false"` opts back in, which is how a control
 * nested inside a marked ancestor returns Enter to the wizard.
 */
export const CONSUMES_ENTER_KEY_ATTRIBUTE = "data-consumes-enter-key";

const CONSUMING_SELECTOR = `textarea, [role="combobox"], [${CONSUMES_ENTER_KEY_ATTRIBUTE}="true"]`;
const OPTED_IN_SELECTOR = `[${CONSUMES_ENTER_KEY_ATTRIBUTE}="false"]`;

/**
 * Whether the element an Enter keydown landed on owns the key, meaning the wizard should leave the
 * event to it rather than advancing to the next field.
 *
 * True for a textarea, for anything at or inside a `role="combobox"`, and for anything at or inside
 * an element tagged `{@link CONSUMES_ENTER_KEY_ATTRIBUTE}="true"`. The combobox check covers a
 * closed trigger and an open dropdown alike: Radix and cmdk move focus into portaled content, so the
 * event target is often a descendant item or search input rather than the trigger, and React bubbles
 * it through the component tree either way.
 *
 * Whichever marker sits nearest the target wins, so an explicit `="false"` inside a consuming
 * ancestor hands Enter back to the wizard.
 */
export const consumesEnterKey = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return false;

  const consuming = target.closest(CONSUMING_SELECTOR);
  if (!consuming) return false;

  const optedIn = target.closest(OPTED_IN_SELECTOR);
  return !optedIn || !consuming.contains(optedIn);
};
